import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, API_URL, type ApiError } from '@/lib/api';

export type AttachmentEntity = 'KRA_RATING' | 'GOAL' | 'ASSESSMENT' | 'ONE_ON_ONE';

export interface Attachment {
  id: string;
  filename: string;
  mime: string;
  sizeBytes: number;
  uploadedByName: string | null;
  createdAt: string;
  /** Resolved server-side; the client never decides who may remove evidence. */
  canDelete: boolean;
}

const target = (entityType: AttachmentEntity, entityId: string) =>
  `entityType=${entityType}&entityId=${entityId}`;

/**
 * A plain link, not a fetch-to-blob.
 *
 * pms.cargofl.com and api.pms.cargofl.com are the same site, so the SameSite=Lax
 * session cookie rides along on the navigation and the browser handles the save
 * dialog itself.
 */
export const attachmentUrl = (id: string) => `${API_URL}/attachments/${id}/file`;

export const useAttachments = (entityType: AttachmentEntity, entityId: string | undefined) =>
  useQuery<Attachment[], ApiError>({
    queryKey: ['attachments', entityType, entityId],
    queryFn: () => api.get<Attachment[]>(`/attachments?${target(entityType, entityId!)}`),
    enabled: Boolean(entityId),
  });

export function useUploadAttachment(entityType: AttachmentEntity, entityId: string) {
  const qc = useQueryClient();
  return useMutation<Attachment, ApiError, File>({
    mutationFn: (file) => {
      const form = new FormData();
      form.append('file', file);
      return api.upload<Attachment>(`/attachments?${target(entityType, entityId)}`, form);
    },
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['attachments', entityType, entityId] }),
  });
}

export function useDeleteAttachment(entityType: AttachmentEntity, entityId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.delete<void>(`/attachments/${id}`),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['attachments', entityType, entityId] }),
  });
}
